import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import * as AppUtilities from '@/utilities/AppUtilities';
import * as AppUtils from '@/utilities/AppUtils';
import { ImageSourcePropType } from 'react-native';

export interface Visual {
  id: string,
  image: ImageSourcePropType,
  status: 'ready' | 'uploading',
  progress: UploadProgress,
}

export interface UploadProgress {
  uploaded: number,
  total: number,
}

export interface UploadProgressUpdate {
  visual: Visual,
  progress: UploadProgress,
}

export interface FinishUploadUpdate {
  visual: Visual,
  newId: string,
}

export interface UploadsState {
  visuals: Visual[],
  isUploading: boolean,
}

const initialState: UploadsState = {
  visuals: [],
  isUploading: false,
} satisfies UploadsState as UploadsState;

export const uploadsSlice = createSlice({
  name: 'uploads',
  initialState,
  reducers: {
    startUpload: (state, visual: PayloadAction<Visual>) => {
      const newItems = state.visuals.slice();
      visual.payload.status = 'uploading';
      newItems.push(visual.payload);
      state.visuals = newItems;
      state.isUploading = true;
    },
    removeUpload: (state, visual: PayloadAction<Visual>) => {
      const newItems = state.visuals.filter((v) => {
        return v.id != visual.payload.id;
      });
      state.visuals = newItems;
    },
    finishUpload: (state, visual: PayloadAction<FinishUploadUpdate>) => {
      state.visuals.forEach((v: Visual) => {
        if (v.id == visual.payload.visual.id) {
          v.status = 'ready';
          v.id = visual.payload.newId;
        }
      });
      state.isUploading = false;
    },
    updateVisuals: (state, visuals: PayloadAction<Visual[]>) => {
      state.visuals = visuals.payload;
    },
    updateUploadProgress: (state, visuals: PayloadAction<UploadProgressUpdate>) => {
      state.visuals.forEach((v: Visual) => {
        if (v.id == visuals.payload.visual.id) {
          v.progress = visuals.payload.progress;
        }
      });
    },
  },
});

export const {
  finishUpload,
  startUpload,
  removeUpload,
  updateVisuals,
  updateUploadProgress,
} = uploadsSlice.actions;

export default uploadsSlice.reducer;
